import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

// eslint-disable-next-line no-restricted-imports
import { additionalCharges } from '../../data/actions';

const CloseIcon = () => {
  return (
    <svg
      viewBox="64 64 896 896"
      focusable="false"
      className=""
      data-icon="close"
      width="1em"
      height="1em"
      fill="#9b9b9b"
      aria-hidden="true"
    >
      <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 00203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z" />
    </svg>
  );
};

const AdditionalChargeRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  padding-bottom: 10px;
  padding-left: 22px;
  padding-right: 22px;
  ${(props) => props.showingLabels && 'padding-top: 10px;'}
  width: 100%;
`;

const AdditionalChargeInput = styled.input`
  border: 1px solid #ccc;
  border-radius: 2px;
  height: 25px;
  outline: none;
  padding-left: 5px;
  text-align: left;
  ${(props) => `width: ${props.width};`}
  ${(props) => props.error && 'border-color: red;'}
`;

const CloseIconButton = styled.button`
  color: #9b9b9b;
  cursor: pointer;
  outline: none;
`;

const CloseIconWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding-top: ${(props) => (props.showingLabels ? '29px' : '5px')};
`;

const ErrorMessage = styled.span`
  color: red;
  padding-left: 26px;
`;

const InputLabel = styled.div`
  color: #9b9b9b;
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 5px;
  text-transform: uppercase;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${(props) => props.showingLabels && 'border-top: 1px solid #ddd;'}
`;

type AdditionalChargeProps = {
  id: string;
  name: string;
  price: number;
  quoteItemId: string;
  showLabels: boolean;
};

const AdditionalCharge = ({
  id,
  name,
  price,
  quoteItemId,
  showLabels,
}: AdditionalChargeProps) => {
  const dispatch = useDispatch();
  const deleteAdditionalCharge = useCallback(
    (data: any) => {
      dispatch(additionalCharges.delete(data));
    },
    [dispatch],
  );
  const updateAdditionalCharge = useCallback(
    (data: any) => {
      dispatch(additionalCharges.update(data));
    },
    [dispatch],
  );
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [localName, setLocalName] = useState('');
  const [localPrice, setLocalPrice] = useState('');
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setLocalName(name);
    setLocalPrice(price);
  }, [name, price]);

  //focus nameInputRef when its value is first assigned
  useEffect(() => {
    if (nameInputRef.current !== null) {
      nameInputRef.current.focus();
    }
  }, []);

  return (
    <Wrapper>
      <AdditionalChargeRow showingLabels={showLabels}>
        <InputWrapper>
          {showLabels && <InputLabel>Additional Charge</InputLabel>}
          <AdditionalChargeInput
            error={errors.nameError}
            value={localName}
            width="235px"
            alignment="left"
            onBlur={() => {
              if (!localName || localName.trim() == '')
                setErrors({ ...errors, nameError: true });
              updateAdditionalCharge({
                additionalChargeId: id,
                quoteItemId: quoteItemId,
                data: {
                  name: localName,
                },
              });
            }}
            onChange={(e) => {
              if (errors.nameError) setErrors({ ...errors, nameError: false });
              setLocalName(e.target.value);
            }}
            ref={nameInputRef}
          />
        </InputWrapper>
        <InputWrapper>
          {showLabels && <InputLabel>Price</InputLabel>}
          <AdditionalChargeInput
            width="120px"
            alignment="left"
            error={errors.priceError}
            value={
              !isNaN(Number(localPrice)) && localPrice.trim() !== ''
                ? `$${localPrice}`
                : ''
            }
            onChange={(e) => {
              if (errors.priceError)
                setErrors({ ...errors, priceError: false });
              if (
                !e.target.value.includes('.') ||
                e.target.value.split('.')[1].length <= 2
              ) {
                setLocalPrice(e.target.value.replace(/[^0-9.]/g, ''));
              }
            }}
            onBlur={() => {
              if (isNaN(parseFloat(localPrice)) || localPrice === '')
                setErrors({ ...errors, priceError: true });
              updateAdditionalCharge({
                additionalChargeId: id,
                quoteItemId: quoteItemId,
                data: {
                  price: localPrice,
                },
              });
            }}
          />
        </InputWrapper>
        <CloseIconWrapper showingLabels={showLabels}>
          {' '}
          <CloseIconButton
            onClick={() =>
              deleteAdditionalCharge({
                additionalChargeId: id,
                quoteItemId: quoteItemId,
              })
            }
          >
            <CloseIcon />
          </CloseIconButton>
        </CloseIconWrapper>
      </AdditionalChargeRow>
      {Object.values(errors).some((errorPresent) => errorPresent) && (
        <ErrorMessage>*Please fill out the highlighted fields</ErrorMessage>
      )}
    </Wrapper>
  );
};

export default AdditionalCharge;
